import { redirect, type Params, type RouteObject } from 'react-router';
import { paths } from '@/config/paths';
import type { RouteHandle } from '@/shared/routing';
import { findDataSource } from './model/dataSources';
import { humanize, schemaLabel } from './model/schema';
import { DATA_TAB, TAB_PARAM } from './model/useAnalyticsSearchParams';

// Titles come from the URL so they are available before the schema has loaded.
const schemaTitle = ({ schema }: Params) => (schema ? schemaLabel(schema) : 'Schema');
const tableTitle = ({ table }: Params) => (table ? humanize(table) : 'Table');
const recordTitle = ({ id }: Params) => id ?? 'Record';
const sourceTitle = ({ source }: Params) => findDataSource(source)?.title ?? 'Source';

/** The table page, keeping the link's parameters, on `tab` when given. */
function tableRedirect({ schema = '', table = '' }: Params, request: Request, tab?: string) {
  const search = new URL(request.url).searchParams;
  if (tab) search.set(TAB_PARAM, tab);
  const text = search.toString();
  return redirect(`${paths.analytics.object(`${schema}/${table}`)}${text ? `?${text}` : ''}`);
}

export const createAnalyticsRoutes = (): RouteObject[] => [
  {
    path: paths.analytics.root,
    handle: { title: 'Data explorer', breadcrumb: 'Data explorer' } satisfies RouteHandle,
    children: [
      {
        index: true,
        lazy: {
          Component: async () =>
            (await import('./pages/AnalyticsOverviewPage')).AnalyticsOverviewPage,
        },
      },
      {
        // A static segment: it takes precedence over a schema named "source".
        path: 'source/:source',
        handle: { title: sourceTitle, breadcrumb: sourceTitle } satisfies RouteHandle,
        lazy: {
          Component: async () => (await import('./pages/AnalyticsSourcePage')).AnalyticsSourcePage,
        },
      },
      {
        path: ':schema',
        handle: { title: schemaTitle, breadcrumb: schemaTitle } satisfies RouteHandle,
        children: [
          {
            index: true,
            lazy: {
              Component: async () =>
                (await import('./pages/AnalyticsOverviewPage')).AnalyticsOverviewPage,
            },
          },
          {
            path: ':table',
            handle: { title: tableTitle, breadcrumb: tableTitle } satisfies RouteHandle,
            children: [
              {
                index: true,
                lazy: {
                  Component: async () =>
                    (await import('./pages/AnalyticsTablePage')).AnalyticsTablePage,
                },
              },
              {
                // The overview and Data views pages became tabs of the table page; old links
                // land there. Static segments, so they take precedence over records with these IDs.
                path: 'overview',
                loader: ({ params, request }) => tableRedirect(params, request),
              },
              {
                path: 'views',
                loader: ({ params, request }) => tableRedirect(params, request, DATA_TAB),
              },
              {
                path: ':id',
                handle: { title: recordTitle, breadcrumb: recordTitle } satisfies RouteHandle,
                lazy: {
                  Component: async () =>
                    (await import('./pages/AnalyticsRecordPage')).AnalyticsRecordPage,
                },
              },
            ],
          },
        ],
      },
    ],
  },
];
